import { prop } from '@ipfabric/utils/fp/prop';
import {
  type PaginationNumbers,
  buildPaginationNumbers,
} from 'components/Pagination/Pagination';
import {
  RULE_CHAIN_ITEMS_PER_PAGE,
  type SecuritySchemaMergedState,
} from 'components/SecuritySchema/model/SecuritySchemaModel';
import {
  type SecuritySchemaNode,
  isChainItemNode,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { chainNeedsPolicyBoxes } from 'components/SecuritySchema/model/helpers/chainNeedsPolicyBoxes';
import { groupRulesByCloudPolicy } from 'components/SecuritySchema/model/helpers/groupRulesByCloudPolicy';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { selectActiveRuleChain } from './selectActiveRuleChain';
import { selectActiveRuleChainSchemaRoots } from './selectActiveRuleChainSchemaRoots';

interface PaginatedRuleChainSchemaRoots {
  pagination: PaginationNumbers;
  roots: SecuritySchemaNode[] | null;
}

export const selectPaginatedRuleChainSchemaRoots: (
  s: SecuritySchemaMergedState,
) => PaginatedRuleChainSchemaRoots = createScopedSelector(() =>
  createSelector(
    selectActiveRuleChainSchemaRoots,
    selectActiveRuleChain,
    prop('page'),
    (roots, chain, page) => {
      const pagination = buildPaginationNumbers(
        roots?.length ?? 0,
        page,
        RULE_CHAIN_ITEMS_PER_PAGE,
      );
      const firstItem = RULE_CHAIN_ITEMS_PER_PAGE * page;
      const pageRoots =
        roots?.slice(firstItem, firstItem + RULE_CHAIN_ITEMS_PER_PAGE) ?? null;
      if (pageRoots === null) {
        return { pagination, roots: null };
      }
      // Number the page's chain items before grouping, so a box the grouping
      // creates stays unnumbered while one standing for a chain rule keeps its
      // number. Paginate then group, so a page always holds the same rule count
      // whether or not the rules carry a policy.
      const numbered = pageRoots.map((node, index) =>
        isChainItemNode(node) ? { ...node, pageIndex: index } : node,
      );
      return {
        pagination,
        roots: chainNeedsPolicyBoxes(chain)
          ? groupRulesByCloudPolicy(numbered)
          : numbered,
      };
    },
  ),
);
